import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';

import {FormDropdown} from 'src/modules/event-create/attributes-block/attribute-form/possible-value-modal/form-dropdown/form-dropdown';
import {
    createPossibleValueInputs,
} from 'src/modules/event-create/attributes-block/attribute-form/utils/createPossibleValueInputs';
import {getPossibleValues} from 'src/modules/event-create/attributes-block/attribute-form/utils/getPossibleValues';
import {
    updatePossibleValueInput,
} from 'src/modules/event-create/attributes-block/attribute-form/utils/updatePossibleValueInput';
import {PossibleValueInput} from 'src/modules/event-create/attributes-block/typedefs';
import {FieldName} from 'src/modules/event-create/typedefs';
import {possibleValuesToString} from 'src/utils/possibleValuesToString';

import Button from '@pb/kit-component-button';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {useShown} from '@pb/kit-hooks-use-shown';

import {PossibleValuesModalInputItem} from './possible-values-modal-input-item';

type Props = {
    attributeName?: string,
    placeholder: string,
    possibleValues: string[],
    validationHighLight: boolean,
    onChangePossibleValues: (value: string[]) => void,
};

export const PossibleValuesModal = (
    {
        attributeName,
        placeholder,
        onChangePossibleValues,
        possibleValues,
        validationHighLight,
    }: Props) => {

    const {onToggle, shown, onClose} = useShown();
    const currentPossibleValues = useMemo(() => createPossibleValueInputs(possibleValues), [possibleValues]);
    const [modalInputsValues, setModalInputsValues] = useState<PossibleValueInput[]>(currentPossibleValues);
    const possibleValuesInputValue = useMemo(() => possibleValuesToString(possibleValues), [possibleValues]);

    useEffect(() => {
        setModalInputsValues(currentPossibleValues);
    }, [currentPossibleValues]);

    const updateInputValue = (
        event: ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
    ) => {
        const {name, value} = event.target;
        const index = Number(name);

        if (!name || Number.isNaN(index)) {
            return;
        }

        setModalInputsValues(currentValues =>
            updatePossibleValueInput(currentValues, index, value, !value.length));
    };

    const onClearInputValue = (index: number) => {
        setModalInputsValues(currentValues =>
            updatePossibleValueInput(currentValues, index, '', true));
    };

    const onApply = () => {
        onChangePossibleValues(getPossibleValues(modalInputsValues));
        onClose();
    };

    return (
        <FormDropdown {...{
            inputValue: possibleValuesInputValue,
            inputPlaceholder: placeholder,
            inputName: FieldName.POSSIBLE_VALUES,
            onToggle,
            shown,
            validationHighLight,
        }}>
            <Item style={{width: '30rem'}}>
                <Group direction="v">
                    <Item className="pdx-m pt-s">
                        <EllipsisTextHint>
                            <strong>{attributeName}</strong>
                        </EllipsisTextHint>
                    </Item>
                    <Item style={{width: '100%'}} className="pdx-m">
                        <Group direction="v">
                            {modalInputsValues.map((InputValue, index) => (
                                <Item key={index} style={{width: '100%'}}>
                                    <PossibleValuesModalInputItem
                                        index={index}
                                        possibleValue={InputValue}
                                        onBlur={updateInputValue}
                                        onChange={updateInputValue}
                                        onClearInputValue={onClearInputValue}
                                    />
                                </Item>
                            ))}
                        </Group>
                    </Item>
                    <Item style={{width: '100%'}} className="pdx-m pb-m">
                        <Group justify="end">
                            <Button onClick={onApply} variant="Type.PRIMARY">Apply</Button>
                            <Button onClick={onClose} variant="Type.SECONDARY">Cancel</Button>
                        </Group>
                    </Item>
                </Group>
            </Item>
        </FormDropdown>
    );
};
